// src/modules/competition/components/StatisticsView/index.js
import React, { useState } from 'react';
import { Card, Row, Col, ButtonGroup, Button } from 'react-bootstrap';
import { Line, Bar } from 'react-chartjs-2';
import PropTypes from 'prop-types';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    TimeScale
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { useTheme } from '../../../../contexts/ThemeContext';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    TimeScale
);

const ACTIVITY_TYPE_COLORS = {
    running: '#FF6384',
    cycling: '#36A2EB',
    swimming: '#4BC0C0',
    hiking: '#FFCE56',
    walking: '#9966FF',
    default: '#C9CBCF'
};

const StatisticsView = ({
    competition,
    datasets,
    dailyDatasets,
    projectionDatasets,
    gapDatasets,
    totalActivities,
    totalPoints,
    winner,
    finalMargin,
    activityTypeBreakdown
}) => {
    const { theme } = useTheme();
    const isDarkMode = theme === 'dark';
    const [chartMode, setChartMode] = useState('cumulative');
    const [showProjection, setShowProjection] = useState(true);

    const textColor = isDarkMode ? '#e0e0e0' : '#333333';
    const mutedTextColor = isDarkMode ? '#adb5bd' : '#6c757d';
    const gridColor = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';

    const sharedScales = {
        y: {
            beginAtZero: true,
            ticks: { color: textColor },
            grid: { color: gridColor }
        },
        x: {
            type: 'time',
            time: { unit: 'day', displayFormats: { day: 'MMM d' } },
            title: { display: true, text: 'Date', color: textColor },
            ticks: { color: textColor },
            grid: { color: gridColor }
        }
    };

    const mainChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    color: textColor,
                    filter: item => !item.text.includes('(projected)')
                }
            },
            title: {
                display: true,
                text: chartMode === 'cumulative' ? 'Points Progress Over Time' : 'Daily Points',
                color: textColor
            },
            tooltip: {
                callbacks: {
                    label: context => `${context.dataset.label}: ${context.parsed.y.toFixed(1)} pts`
                }
            }
        },
        scales: {
            ...sharedScales,
            y: {
                ...sharedScales.y,
                title: {
                    display: true,
                    text: chartMode === 'cumulative' ? 'Cumulative Points' : 'Points',
                    color: textColor
                }
            }
        }
    };

    const gapChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top', labels: { color: textColor } },
            title: { display: true, text: 'Points Behind the Leader', color: textColor },
            tooltip: {
                callbacks: {
                    label: context => `${context.dataset.label}: ${context.parsed.y.toFixed(1)} pts behind`
                }
            }
        },
        scales: {
            ...sharedScales,
            y: {
                ...sharedScales.y,
                title: { display: true, text: 'Points Behind Leader', color: textColor }
            }
        }
    };

    const mainChartDatasets = chartMode === 'cumulative'
        ? [...datasets, ...(showProjection ? projectionDatasets : [])]
        : dailyDatasets;
    const hasMainData = mainChartDatasets.some(d => d.data.length > 0);
    const hasGapData = gapDatasets && gapDatasets.length > 0;

    // Activity breakdown bar chart
    const competitorNames = Object.keys(activityTypeBreakdown || {});
    const allTypes = [...new Set(competitorNames.flatMap(name => Object.keys(activityTypeBreakdown[name])))];
    const hasActivityBreakdown = competitorNames.length > 0 && allTypes.length > 0;

    const activityBarData = {
        labels: competitorNames,
        datasets: allTypes.map(type => ({
            label: type.charAt(0).toUpperCase() + type.slice(1),
            data: competitorNames.map(name => activityTypeBreakdown[name][type]?.count || 0),
            backgroundColor: ACTIVITY_TYPE_COLORS[type.toLowerCase()] || ACTIVITY_TYPE_COLORS.default
        }))
    };

    const activityBarOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top', labels: { color: textColor } },
            title: { display: true, text: 'Activities by Type', color: textColor },
            tooltip: {
                callbacks: {
                    label: context => `${context.dataset.label}: ${context.parsed.y} activities`
                }
            }
        },
        scales: {
            x: { stacked: true, ticks: { color: textColor }, grid: { color: gridColor } },
            y: {
                stacked: true,
                beginAtZero: true,
                title: { display: true, text: 'Activities', color: textColor },
                ticks: { color: textColor, stepSize: 1 },
                grid: { color: gridColor }
            }
        }
    };

    const isActive = competition.daysRemaining > 0;

    return (
        <div>
            <Card className="mb-4">
                <Card.Header>
                    <h4 className="mb-0">Competition Overview</h4>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={3} sm={6} className="mb-3 mb-md-0">
                            <div className="text-center">
                                <h6 className="mb-1" style={{ color: mutedTextColor }}>Total Activities</h6>
                                <p className="h2 mb-0">{totalActivities}</p>
                            </div>
                        </Col>
                        <Col md={3} sm={6} className="mb-3 mb-md-0">
                            <div className="text-center">
                                <h6 className="mb-1" style={{ color: mutedTextColor }}>Total Points</h6>
                                <p className="h2 mb-0">{totalPoints.toFixed(1)}</p>
                            </div>
                        </Col>
                        <Col md={3} sm={6} className="mb-3 mb-md-0">
                            <div className="text-center">
                                <h6 className="mb-1" style={{ color: mutedTextColor }}>Competitors</h6>
                                <p className="h2 mb-0">{competition.competitors?.length || 0}</p>
                            </div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-center">
                                {isActive ? (
                                    <>
                                        <h6 className="mb-1" style={{ color: mutedTextColor }}>Days Remaining</h6>
                                        <p className="h2 mb-0">{competition.daysRemaining}</p>
                                    </>
                                ) : winner ? (
                                    <>
                                        <h6 className="mb-1" style={{ color: mutedTextColor }}>Winner</h6>
                                        <p className="h2 mb-0">{winner}</p>
                                        {finalMargin !== null && (
                                            <small style={{ color: mutedTextColor }}>by {finalMargin} pts</small>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <h6 className="mb-1" style={{ color: mutedTextColor }}>Status</h6>
                                        <p className="h4 mb-0">Concluded</p>
                                    </>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="mb-4">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h4 className="mb-0">Points Progress</h4>
                    <div className="d-flex gap-2">
                        {chartMode === 'cumulative' && projectionDatasets.length > 0 && (
                            <label style={{
                                display: 'flex', alignItems: 'center', gap: '8px',
                                cursor: 'pointer', fontSize: '0.875rem', color: mutedTextColor,
                                userSelect: 'none'
                            }}>
                                <span>Projection</span>
                                <span
                                    onClick={() => setShowProjection(p => !p)}
                                    style={{
                                        position: 'relative', display: 'inline-block',
                                        width: '36px', height: '20px', flexShrink: 0
                                    }}
                                >
                                    <span style={{
                                        position: 'absolute', inset: 0, borderRadius: '20px',
                                        backgroundColor: showProjection ? '#3498db' : (isDarkMode ? '#444' : '#ccc'),
                                        transition: 'background-color 0.2s'
                                    }} />
                                    <span style={{
                                        position: 'absolute', top: '3px',
                                        left: showProjection ? '19px' : '3px',
                                        width: '14px', height: '14px', borderRadius: '50%',
                                        backgroundColor: '#fff',
                                        transition: 'left 0.2s',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                                    }} />
                                </span>
                            </label>
                        )}
                        <ButtonGroup size="sm">
                            <Button
                                variant={chartMode === 'cumulative' ? 'primary' : 'outline-secondary'}
                                onClick={() => setChartMode('cumulative')}
                            >
                                Cumulative
                            </Button>
                            <Button
                                variant={chartMode === 'daily' ? 'primary' : 'outline-secondary'}
                                onClick={() => setChartMode('daily')}
                            >
                                Daily
                            </Button>
                        </ButtonGroup>
                    </div>
                </Card.Header>
                <Card.Body>
                    {hasMainData ? (
                        <div style={{ minHeight: '300px', height: '50vh', maxHeight: '500px' }}>
                            <Line data={{ datasets: mainChartDatasets }} options={mainChartOptions} />
                        </div>
                    ) : (
                        <div className="text-center py-4" style={{ color: mutedTextColor }}>
                            No activity data available yet.
                        </div>
                    )}
                </Card.Body>
            </Card>

            {hasGapData && (
                <Card className="mb-4">
                    <Card.Header>
                        <h4 className="mb-0">Points Gap from Leader</h4>
                    </Card.Header>
                    <Card.Body>
                        <div style={{ minHeight: '250px', height: '40vh', maxHeight: '400px' }}>
                            <Line data={{ datasets: gapDatasets }} options={gapChartOptions} />
                        </div>
                    </Card.Body>
                </Card>
            )}

            {hasActivityBreakdown && (
                <Card className="mb-4">
                    <Card.Header>
                        <h4 className="mb-0">Activity Breakdown</h4>
                    </Card.Header>
                    <Card.Body>
                        <div style={{ minHeight: '200px', height: '35vh', maxHeight: '350px' }}>
                            <Bar data={activityBarData} options={activityBarOptions} />
                        </div>
                    </Card.Body>
                </Card>
            )}
        </div>
    );
};

StatisticsView.propTypes = {
    competition: PropTypes.shape({
        competitors: PropTypes.array,
        daysRemaining: PropTypes.number.isRequired
    }).isRequired,
    datasets: PropTypes.array.isRequired,
    dailyDatasets: PropTypes.array.isRequired,
    projectionDatasets: PropTypes.array.isRequired,
    gapDatasets: PropTypes.array.isRequired,
    totalActivities: PropTypes.number.isRequired,
    totalPoints: PropTypes.number.isRequired,
    winner: PropTypes.string,
    finalMargin: PropTypes.number,
    activityTypeBreakdown: PropTypes.object.isRequired
};

export default StatisticsView;
